/**
 * Browser GPS accuracy policy (95% radius in metres, from `position.coords.accuracy`).
 * UX gate only: accuracy never enters the serviceability decision or the distance calculation,
 * and a missing/unusable value never blocks the flow. Mirrors the backend
 * `LocationAccuracyBands` constants; keep both in sync.
 */

/** <= this is a normal fix: resolve without a caveat. */
export const ACCURACY_NORMAL_MAX_METERS = 100

/** <= this is degraded: resolve, but tell the customer to check the suggestion. */
export const ACCURACY_DEGRADED_MAX_METERS = 300

/** Sanity ceiling for what is sent to the backend; wider values are treated as missing. */
export const ACCURACY_SANITY_MAX_METERS = 100_000

export type LocationAccuracyBand = "normal" | "degraded" | "poor" | "missing"

/**
 * Normalises the browser value: finite, non-negative, within sanity bounds → integer metres;
 * anything else → undefined (missing).
 */
export function sanitizeAccuracyMeters(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined
  if (value < 0 || value > ACCURACY_SANITY_MAX_METERS) return undefined
  return Math.round(value)
}

export function classifyAccuracy(accuracyMeters: number | undefined): LocationAccuracyBand {
  const sanitized = sanitizeAccuracyMeters(accuracyMeters)
  if (sanitized === undefined) return "missing"
  if (sanitized <= ACCURACY_NORMAL_MAX_METERS) return "normal"
  if (sanitized <= ACCURACY_DEGRADED_MAX_METERS) return "degraded"
  return "poor"
}
